import {
  Box,
  FormControlLabel,
  IconButton,
  SvgIcon,
  Typography,
} from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FullScreenLoader, ToggleSwitch } from '@Components/index';
import MainSectionLayout from '@Components/MainSectionLayout';
import { Icons } from '@Constants/index';
import CallMonitorPercentageEditModal from '@Containers/Settings/AdminSettings/CallMonitorPercentageEditModal';
import { CallMonitorPercentageEditDataProps } from '@Containers/Settings/AdminSettings/CallMonitorPercentageEditModal/CallMonitorPercentageEditModal.types';
import CallTimeFrameEditModal from '@Containers/Settings/AdminSettings/CallTimeFrameEditModal';
import { CallTimeFrameEditDataProps } from '@Containers/Settings/AdminSettings/CallTimeFrameEditModal/CallTimeFrameEditModal.types';
import { StyledStack } from '@Containers/Settings/AdminSettings/FeatureFlags/FeatureFlags.styles';
import { FeatureFlagData } from '@Models/settings.models';
import {
  useGetFeatureFlagsQuery,
  useToggleFeatureFlagStateByIdMutation,
} from '@Services/settings.service';
import { BaseAlertProps, FeatureFlagTypes } from '@Typings/global.types';
import { getDateMMDDYYYYHHMM } from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';

const FeatureFlags: FunctionComponent = (): JSX.Element => {
  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  /** RTK QUERY HOOK */
  // load a list of feature flags
  const {
    data: dataOfFeatureFlagsList = [],
    isLoading: loadingFeatureFlagsList,
    isFetching: fetchingFeatureFlagsList,
    isError: isErrorForGetFeatureFlagsList,
    error: errorForGetFeatureFlagsList,
  } = useGetFeatureFlagsQuery();

  // toggling of feature flags
  const [toggleFeatureFlagStateById, toggleFeatureFlagStateByIdMethods] =
    useToggleFeatureFlagStateByIdMutation();

  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [showCallTimeFrameEditModal, setShowCallTimeFrameEditModal] =
    useState<boolean>(false);
  const [callTimeFrameEditModalData, setCallTimeFrameEditModalData] =
    useState<CallTimeFrameEditDataProps>({ id: '', name: '', value: '' });
  const [
    showCallMonitorPercentageEditModal,
    setShowCallMonitorPercentageEditModal,
  ] = useState<boolean>(false);
  const [
    callMonitorPercentageEditModalData,
    setCallMonitorPercentageEditModalData,
  ] = useState<CallMonitorPercentageEditDataProps>({
    id: '',
    name: '',
    value: '',
  });

  const specialRows = [
    FeatureFlagTypes.CALL_TIME_FRAME,
    FeatureFlagTypes.CALL_DURATION_MONITORING,
  ];

  /** Handles toggle enabling state change */
  const toggleFeatureFlagState = (id?: string, state?: boolean) => {
    if (id && state !== null && state !== undefined) {
      return toggleFeatureFlagStateById({
        id,
        state,
      });
    }
  };

  const editFeatureFlagValues = (id: string, name: string, value: string) => {
    if ((name as FeatureFlagTypes) === FeatureFlagTypes.CALL_TIME_FRAME) {
      setShowCallTimeFrameEditModal(true);
      setCallTimeFrameEditModalData({ id, name, value });
    } else if (
      (name as FeatureFlagTypes) === FeatureFlagTypes.CALL_DURATION_MONITORING
    ) {
      setShowCallMonitorPercentageEditModal(true);
      setCallMonitorPercentageEditModalData({ id, name, value });
    }
  };

  /**
   * Error Alerts for the Get Feature Flag Query
   */
  useEffect(() => {
    if (isErrorForGetFeatureFlagsList && errorForGetFeatureFlagsList) {
      triggerAlert(errorForGetFeatureFlagsList, setAlertState);
    }
  }, [
    triggerAlert,
    errorForGetFeatureFlagsList,
    isErrorForGetFeatureFlagsList,
  ]);

  /**
   * Error and Success alerts for toggling feature flag state
   */
  useEffect(() => {
    if (
      toggleFeatureFlagStateByIdMethods.isError &&
      toggleFeatureFlagStateByIdMethods.error
    ) {
      triggerAlert(toggleFeatureFlagStateByIdMethods.error, setAlertState);
    }
  }, [
    toggleFeatureFlagStateByIdMethods.isError,
    toggleFeatureFlagStateByIdMethods.isSuccess,
    toggleFeatureFlagStateByIdMethods.error,
    toggleFeatureFlagStateByIdMethods.data,
    triggerAlert,
  ]);

  /**
   * Constructs the UI (row) of the feature flag table
   * @param id unique id of the caller
   * @param name name of the feature flag (setting)
   * @param value value of the feature flag (setting)
   * @param updatedAt updatedAt date
   * @param state state of setting
   * @param handleFeatureFlagsToggling callback method to toggle feature flag (setting) by unique id
   * @param editFeatureFlag callback method to edit value of the feature flag (setting) by unique id
   * @returns a single row, populating the data
   */
  const FeatureFlagTableRow = (
    id: string,
    updatedAt: string,
    name: string,
    value: string,
    state: boolean,
    handleFeatureFlagsToggling: (id: string, state: boolean) => void,
    editFeatureFlag: (id: string, name: string, value: string) => void
  ): JSX.Element => (
    <StyledStack key={id}>
      <Typography className="date" variant="main">
        {t('adminSettings.Updated Date', {
          interpolation: {
            escapeValue: false,
          },
          date: getDateMMDDYYYYHHMM(new Date(updatedAt), true),
        })}
      </Typography>

      <Typography
        className="feature-name"
        variant="main"
        sx={{ color: state ? 'text.primary' : 'text.disabled' }}
      >
        {name}
      </Typography>

      {(name as FeatureFlagTypes) === FeatureFlagTypes.CALL_TIME_FRAME && (
        <Box
          className="edit-feature-flag-btn"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Typography
            variant="main"
            sx={{ color: state ? 'text.primary' : 'text.disabled' }}
          >
            {value.toUpperCase()}
          </Typography>
          <IconButton
            sx={{
              color: 'common.black',
            }}
            onClick={() => editFeatureFlag(id, name, value)}
          >
            <SvgIcon>{Icons.edit}</SvgIcon>
          </IconButton>
        </Box>
      )}

      {(name as FeatureFlagTypes) ===
        FeatureFlagTypes.CALL_DURATION_MONITORING && (
        <Box
          className="edit-feature-flag-btn"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Typography
            variant="main"
            sx={{ color: state ? 'text.primary' : 'text.disabled' }}
          >
            {value.toUpperCase()}
          </Typography>
          <IconButton
            sx={{
              color: 'common.black',
            }}
            onClick={() => editFeatureFlag(id, name, value)}
          >
            <SvgIcon>{Icons.edit}</SvgIcon>
          </IconButton>
        </Box>
      )}

      {!specialRows.includes(name as FeatureFlagTypes) && (
        <Box sx={{ minWidth: '150px' }} />
      )}

      <Box className="enable-feature-flag">
        <FormControlLabel
          control={
            <ToggleSwitch
              checked={state}
              onChange={() => handleFeatureFlagsToggling(id, state)}
              color="secondary"
            />
          }
          label={
            <Typography variant="main">
              {t(
                state ? 'common.messages.Enabled' : 'common.messages.Disabled'
              )}
            </Typography>
          }
        />
      </Box>
    </StyledStack>
  );

  /**
   * Table component to render UpdatedAt, Name and State of the flag feature
   */
  const TableComponent = (
    <>
      <Box sx={{ overflowX: 'auto', minWidth: '100%' }}>
        {dataOfFeatureFlagsList.length > 0 &&
          dataOfFeatureFlagsList.map(
            (item: FeatureFlagData): JSX.Element =>
              FeatureFlagTableRow(
                item.id,
                item.updatedAt,
                item.name,
                item.value,
                item.state,
                toggleFeatureFlagState,
                editFeatureFlagValues
              )
          )}
        {dataOfFeatureFlagsList.length === 0 && (
          <Typography
            variant="main"
            fontWeight="bold"
            textAlign="center"
            sx={{ my: 3.2, color: 'action.disabled' }}
          >
            {t('common.table.No data to show')}
          </Typography>
        )}
      </Box>
      <CallTimeFrameEditModal
        callTimeFrameEditData={callTimeFrameEditModalData}
        isOpen={showCallTimeFrameEditModal}
        setIsOpen={setShowCallTimeFrameEditModal}
      />
      <CallMonitorPercentageEditModal
        callMonitorPercentageEditData={callMonitorPercentageEditModalData}
        isOpen={showCallMonitorPercentageEditModal}
        setIsOpen={setShowCallMonitorPercentageEditModal}
      />
    </>
  );

  return (
    <>
      {loadingFeatureFlagsList || fetchingFeatureFlagsList ? (
        <FullScreenLoader data-testid="loader" />
      ) : (
        <>
          <MainSectionLayout
            alertState={alertState}
            table={TableComponent}
            tabChild
          />
        </>
      )}
    </>
  );
};

export default FeatureFlags;
